/**
 * 환경설정 — 글꼴·글자 크기
 * 기관 기본 글꼴 + 권장 크기, Before/After 미리보기
 * (글자 크기 개인 덮어쓰기는 사이드바 A-/A+)
 */

import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { theme as staticTheme } from 'styles/theme';
import { ActionButton } from 'styles/common.styles';
import {
  ERP_FONTS,
  FONT_SIZE_PRESETS,
  ensureAllErpFontsLoaded,
  fontFamilyStack,
  getErpFont,
  normalizeFontSizeRootPx,
  describeTypographyDelta,
} from 'styles/erpFonts';
import { defaultAppearanceSettings } from 'styles/appearanceSlots';

const Block = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BlockTitle = styled.h3`
  margin: 0;
  font-size: ${staticTheme.typography.fontSize.md};
  font-weight: 700;
  color: ${(p) => p.theme.colors.text.primary};
`;

const Hint = styled.p`
  margin: 0;
  font-size: ${staticTheme.typography.fontSize.xs};
  color: ${(p) => p.theme.colors.text.tertiary};
  line-height: 1.45;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const FontGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
`;

const FontCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 10px 12px;
  border: 1px solid ${(p) => (p.$active ? p.theme.colors.primary : p.theme.colors.border)};
  border-radius: ${staticTheme.borderRadius.sm};
  background: ${(p) => (p.$active ? p.theme.colors.primaryLight : p.theme.colors.background.white)};
  cursor: pointer;
  text-align: left;
`;

const FontName = styled.div`
  font-size: ${staticTheme.typography.fontSize.sm};
  font-weight: 700;
  color: ${(p) => p.theme.colors.text.primary};
`;

const FontSample = styled.div`
  font-family: ${(p) => p.$family};
  font-size: 15px;
  line-height: 1.45;
  color: ${(p) => p.theme.colors.text.primary};
  letter-spacing: -0.01em;
`;

const FontMeta = styled.div`
  font-size: ${staticTheme.typography.fontSize.xxs};
  color: ${(p) => p.theme.colors.text.tertiary};
  line-height: 1.4;
`;

const SizeChip = styled.button`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 72px;
  padding: 6px 10px;
  border: 1px solid ${(p) => (p.$active ? p.theme.colors.primary : p.theme.colors.border)};
  border-radius: ${staticTheme.borderRadius.sm};
  background: ${(p) => (p.$active ? p.theme.colors.primaryLight : p.theme.colors.background.white)};
  cursor: pointer;
  color: ${(p) => (p.$active ? p.theme.colors.primary : p.theme.colors.text.primary)};
`;

const SizeLabel = styled.span`
  font-size: ${staticTheme.typography.fontSize.xs};
  font-weight: 700;
`;

const SizePx = styled.span`
  font-size: ${staticTheme.typography.fontSize.xxs};
  color: ${(p) => p.theme.colors.text.tertiary};
`;

const Toggle = styled.button`
  position: relative;
  width: 40px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: ${staticTheme.borderRadius.pill};
  background: ${(p) => (p.$on ? p.theme.colors.primary : p.theme.colors.senary)};
  cursor: pointer;
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${(p) => (p.$on ? '20px' : '2px')};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${(p) => p.theme.colors.white};
  }
`;

const CompareRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const CompareCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const CompareHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const CompareTag = styled.span`
  font-size: ${staticTheme.typography.fontSize.xs};
  font-weight: 700;
  color: ${(p) => (p.$after ? p.theme.colors.primary : p.theme.colors.text.secondary)};
`;

const CompareMeta = styled.span`
  font-size: ${staticTheme.typography.fontSize.xxs};
  color: ${(p) => p.theme.colors.text.tertiary};
`;

const DeltaBar = styled.div`
  padding: 8px 10px;
  border-radius: ${staticTheme.borderRadius.sm};
  background: ${(p) => p.theme.colors.background.medium};
  font-size: ${staticTheme.typography.fontSize.xs};
  color: ${(p) => p.theme.colors.text.secondary};
  line-height: 1.45;
`;

/** rem이 아닌 em — 페이지 html font-size와 격리된 미리보기 */
const Isolate = styled.div`
  font-family: ${(p) => p.$family};
  font-size: ${(p) => p.$rootPx}px;
  line-height: 1.4;
  color: ${(p) => p.theme.colors.text.primary};
  background: ${(p) => p.theme.colors.background.white};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${staticTheme.borderRadius.md};
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 0.55em;
`;

const MiniChrome = styled.div`
  display: grid;
  grid-template-columns: 6.2em 1fr;
  gap: 0.55em;
  min-height: 11em;
`;

const MiniSide = styled.div`
  padding: 0.4em;
  border-radius: 4px;
  background: ${(p) => p.theme.colors.background.sidebar};
  border: 1px solid ${(p) => p.theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: 0.15em;
  font-size: ${12 / 13}em;
`;

const MiniMenu = styled.div`
  padding: 0.3em 0.4em;
  border-radius: 3px;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  background: ${(p) => (p.$active ? p.theme.colors.primaryLight : 'transparent')};
  color: ${(p) => (p.$active ? p.theme.colors.primary : p.theme.colors.text.secondary)};
`;

const MiniMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45em;
  min-width: 0;
`;

const MiniTab = styled.span`
  font-size: ${12 / 13}em;
  font-weight: 600;
  color: ${(p) => p.theme.colors.primary};
  border-bottom: 2px solid ${(p) => p.theme.colors.primary};
  padding: 0 0.15em 0.25em;
  align-self: flex-start;
`;

const MiniToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35em;
  flex-wrap: wrap;
`;

const MiniBtn = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  min-height: 26px;
  border-radius: 3px;
  border: 1px solid ${(p) => p.theme.colors.primary};
  background: ${(p) => p.theme.colors.primary};
  color: ${(p) => p.theme.colors.white};
  font-weight: 600;
  font-size: ${12 / 13}em;
  box-sizing: border-box;
`;

const MiniGhost = styled(MiniBtn)`
  background: ${(p) => p.theme.colors.background.white};
  color: ${(p) => p.theme.colors.text.secondary};
  border-color: ${(p) => p.theme.colors.border};
`;

const MiniBody = styled.div`
  font-size: 1em;
`;

const MiniGrid = styled.div`
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 4px;
  overflow: hidden;
  font-size: ${12 / 13}em;
`;

const MiniHead = styled.div`
  display: grid;
  grid-template-columns: 2.2em 1fr 4.2em;
  background: ${(p) => p.theme.colors.grid.headerActiveBg};
  color: ${(p) => p.theme.colors.grid.headerActiveFg};
  font-weight: 600;
`;

const MiniCell = styled.div`
  padding: 0.35em 0.45em;
`;

const MiniRow = styled.div`
  display: grid;
  grid-template-columns: 2.2em 1fr 4.2em;
  border-top: 1px solid ${(p) => p.theme.colors.border};
  background: ${(p) => (p.$alt ? p.theme.colors.grid.rowHover : 'transparent')};
`;

const MiniNote = styled.div`
  font-size: ${11 / 13}em;
  color: ${(p) => p.theme.colors.text.tertiary};
`;

function MiniErpPreview({ fontFamilyId, fontSizeRootPx }) {
  const family = fontFamilyStack(fontFamilyId);
  const rootPx = normalizeFontSizeRootPx(fontSizeRootPx);
  return (
    <Isolate $family={family} $rootPx={rootPx}>
      <MiniChrome>
        <MiniSide>
          <MiniMenu>인사관리</MiniMenu>
          <MiniMenu $active>예산관리</MiniMenu>
          <MiniMenu>기준정보</MiniMenu>
        </MiniSide>
        <MiniMain>
          <MiniTab>편성요구</MiniTab>
          <MiniToolbar>
            <MiniBtn>조회</MiniBtn>
            <MiniGhost>엑셀</MiniGhost>
          </MiniToolbar>
          <MiniBody>2026년 인건비 편성요구 — 1,200천원</MiniBody>
          <MiniGrid>
            <MiniHead>
              <MiniCell>No</MiniCell>
              <MiniCell>과목</MiniCell>
              <MiniCell>금액</MiniCell>
            </MiniHead>
            <MiniRow>
              <MiniCell>1</MiniCell>
              <MiniCell>인건비</MiniCell>
              <MiniCell>1,200</MiniCell>
            </MiniRow>
            <MiniRow $alt>
              <MiniCell>2</MiniCell>
              <MiniCell>운영비</MiniCell>
              <MiniCell>800</MiniCell>
            </MiniRow>
          </MiniGrid>
          <MiniNote>버튼 높이 26px · 패딩 3×8 — 글자만 커집니다</MiniNote>
        </MiniMain>
      </MiniChrome>
    </Isolate>
  );
}

/**
 * @param {{
 *   draft: import('styles/appearanceSlots').AppearanceSettings,
 *   saved?: import('styles/appearanceSlots').AppearanceSettings,
 *   onChange: (next: import('styles/appearanceSlots').AppearanceSettings) => void,
 *   scope?: 'tenant'|'personal',
 * }} props
 */
export default function AppearanceTypographyPanel({ draft, saved, onChange, scope = 'tenant' }) {
  useEffect(() => {
    ensureAllErpFontsLoaded();
  }, []);

  const before = useMemo(
    () => ({ ...defaultAppearanceSettings(), ...(saved || draft || {}) }),
    [saved, draft]
  );
  const after = draft || before;

  const delta = useMemo(() => describeTypographyDelta(before, after), [before, after]);
  const afterFont = getErpFont(after.fontFamilyId);
  const afterPx = normalizeFontSizeRootPx(after.fontSizeRootPx);

  const commit = (patch) => {
    onChange?.({ ...after, ...patch });
  };

  return (
    <Block>
      <BlockTitle>글꼴 · 글자 크기</BlockTitle>
      <Hint>
        Ctrl+마우스 휠은 화면 전체 비율을 줄여 버튼·그리드까지 같이 작아집니다. 여기서는{' '}
        <strong>글자만</strong> 바꿉니다. 글꼴은 기관 공통(이 화면에서 저장)이고, 글자 크기는 각 PC에서
        사이드바 하단 A−/A+로 덮어쓸 수 있습니다.
      </Hint>

      <Hint style={{ marginTop: 2 }}>
        목록은 SIL OFL 등 <strong>무료·상업적 이용이 가능한</strong> 한글 폰트만 포함합니다.
      </Hint>

      <FontGrid>
        {ERP_FONTS.map((font) => (
          <FontCard
            key={font.id}
            type="button"
            $active={(after.fontFamilyId || 'pretendard') === font.id}
            onClick={() => commit({ fontFamilyId: font.id })}
            title={`${font.label} · ${font.license}`}
          >
            <FontName>{font.label}</FontName>
            <FontSample $family={fontFamilyStack(font.id)}>가나다 ABC 123</FontSample>
            <FontMeta>
              {font.license} · {font.description}
            </FontMeta>
          </FontCard>
        ))}
      </FontGrid>

      <Hint>기관 권장 본문 크기입니다. 저장하면 개인 설정이 없는 사용자에게 적용됩니다.</Hint>
      <Row>
        {FONT_SIZE_PRESETS.map((p) => (
          <SizeChip
            key={p.px}
            type="button"
            $active={afterPx === p.px}
            onClick={() => commit({ fontSizeRootPx: p.px })}
            title={`본문 ${p.px}px`}
          >
            <SizeLabel>{p.label}</SizeLabel>
            <SizePx>{p.px}px</SizePx>
          </SizeChip>
        ))}
      </Row>

      {scope === 'tenant' ? (
        <Row>
          <Toggle
            type="button"
            $on={after.allowPersonalFontSize !== false}
            aria-pressed={after.allowPersonalFontSize !== false}
            onClick={() =>
              commit({ allowPersonalFontSize: after.allowPersonalFontSize === false })
            }
          />
          <Hint as="span">
            {after.allowPersonalFontSize === false
              ? '개인 PC 글자 크기 고정 (사이드바 A−/A+ 비활성)'
              : '개인 PC에서 글자 크기 조절 허용 (사이드바 A−/A+)'}
          </Hint>
        </Row>
      ) : null}

      <BlockTitle>미리보기 — 변경 전 / 후</BlockTitle>
      <Hint>
        왼쪽이 저장되어 있는 현재 값, 오른쪽이 지금 고른 값입니다. 조회 버튼 박스 크기는 같고 글자만 달라지는지
        확인하세요.
      </Hint>
      <DeltaBar>{delta}</DeltaBar>
      <CompareRow>
        <CompareCol>
          <CompareHead>
            <CompareTag>변경 전</CompareTag>
            <CompareMeta>
              {getErpFont(before.fontFamilyId).label} · {normalizeFontSizeRootPx(before.fontSizeRootPx)}px
            </CompareMeta>
          </CompareHead>
          <MiniErpPreview fontFamilyId={before.fontFamilyId} fontSizeRootPx={before.fontSizeRootPx} />
        </CompareCol>
        <CompareCol>
          <CompareHead>
            <CompareTag $after>변경 후</CompareTag>
            <CompareMeta>
              {afterFont.label} · {afterPx}px
            </CompareMeta>
          </CompareHead>
          <MiniErpPreview fontFamilyId={after.fontFamilyId} fontSizeRootPx={after.fontSizeRootPx} />
        </CompareCol>
      </CompareRow>

      <Row>
        <ActionButton
          type="button"
          onClick={() =>
            commit({
              fontFamilyId: 'pretendard',
              fontSizeRootPx: 13,
              allowPersonalFontSize: true,
            })
          }
        >
          글꼴·크기 기본값
        </ActionButton>
      </Row>
    </Block>
  );
}
